import React, { useState, useEffect, useMemo } from "react";
import { Field } from "react-final-form";
import { FormText } from "reactstrap";
import { TextInput } from "../../../../components/inputs/final_form/TextInput";
import { required } from "../../../../util/validators";
import { LoadingButton } from "../../../../components/LoadingButton";
import { SelectInput } from "../../../../components/inputs/final_form/SelectInput";
import { Switch as SwitchInput } from "../../../../components/inputs/final_form/Switch";
import { formatDateTime } from "../../../../util/formatters";
import { AdministrateSelectInput, AdminOptionableInput } from "../../../components/inputs/AdministrateSelectInput";
import { adminApi } from "../../../../util/api";

export const AutoStatusParsingSettingForm = ({ handleSubmit, submitting, values, form }) => {
  const [status, setStatus] = useState("");
  const [substatusOptions, setSubstatusOptions] = useState([]);
  const initialPayorOptions = useMemo(() => {
    return values?.payors ? values?.payors.map((payor) => ({ label: payor.name, value: payor.id })) : [];
  }, [values?.payors]);

  const getSubstatusOptions = async (status, setSubstatusOptions) => {
    try {
      const { data } = await adminApi.get(`/admin/enums/prior_auth_substatuses.json?status=${status}`);
      setSubstatusOptions(data.resources);
    } catch (err) {
      console.error(err);
    }
  };

  const clearField = (fieldName) => {
    form.change(fieldName, "");
  };

  const handleStatusUpdate = (selected) => {
    setSubstatusOptions([]);
    clearField("substatus");

    if (selected?.value !== "denied") {
      clearField("denialReasonCode");
    }

    setStatus(selected?.value);
  };

  const handleServiceUpdate = (selected) => {
    if (selected?.value !== "pa_on_file") {
      clearField("paOnFileProvider");
      form.change("retryPaOnFile", false);
    }
  };

  const handleAssignToFollowUpChange = (shouldAssign) => {
    if (shouldAssign) {
      clearField("status");
      clearField("substatus");
      clearField("denialReasonCode");
      form.change("reassignToPas", false);
    }

    form.change("assignToFollowup", shouldAssign);
  };

  const handleReassignToPasChange = (shouldReassign) => {
    if (shouldReassign) {
      clearField("status");
      clearField("substatus");
      clearField("denialReasonCode");
      form.change("assignToFollowup", false);
    }

    form.change("reassignToPas", shouldReassign);
  };

  useEffect(() => {
    const currentStatus = status || values.status;
    if (currentStatus) {
      getSubstatusOptions(currentStatus, setSubstatusOptions);
    }
  }, [status, values]);

  //  Field display rules:
  //  - Status will only appear when Assign to Follow Up is false
  //  - Only one of Assign to Follow Up or Reassign to PAS can be true
  //  - Sub Status will only appear when the Status allows a sub status
  //  - Denial Reason only appears when the Status is "denied"
  return (
    <>
      <Field component={TextInput} label="Message Regex" name="messageRegex" required validate={required} />
      <Field
        component={AdministrateSelectInput}
        resourceUrl="/admin/enums/auto_status_parsing_setting_services.json"
        label="Service"
        name="service"
        labelKey="label"
        onUpdate={handleServiceUpdate}
      >
        <FormText>Leaving this field blank means the setting will apply to both SuperDial and Medsender handling.</FormText>
      </Field>

      <Field
        component={AdminOptionableInput}
        resource="payors"
        searchParam="name"
        label="Payors"
        kept={true}
        isMulti
        name="payorIds"
        initialOptions={initialPayorOptions}
      />

      {values.service === "pa_on_file" && (
        <>
          <Field
            component={AdministrateSelectInput}
            resourceUrl="/admin/enums/pa_on_file_providers.json"
            label="PA on File Provider"
            name="paOnFileProvider"
            labelKey="label"
          />
          <Field component={SwitchInput} label="Retry PA on File Provider" name="retryPaOnFile" type="checkbox" />
        </>
      )}

      <Field
        component={SwitchInput}
        label="Assign to Follow Up"
        name="assignToFollowup"
        type="checkbox"
        onChange={(e) => handleAssignToFollowUpChange(e.target.checked)}
      />
      <Field
        component={SwitchInput}
        label="Reassign to PAS"
        name="reassignToPas"
        type="checkbox"
        onChange={(e) => handleReassignToPasChange(e.target.checked)}
      />

      <Field component={TextInput} label="Automated Comment" name="automatedComment" />

      {!values.assignToFollowup && !values.reassignToPas && (
        <>
          <Field
            component={AdministrateSelectInput}
            resourceUrl="/admin/enums/prior_auth_statuses.json"
            label="Status"
            name="status"
            labelKey="label"
            onUpdate={handleStatusUpdate}
          />

          {substatusOptions?.length > 0 && (
            <Field component={SelectInput} label="Sub Status" name="substatus" options={substatusOptions} includeBlank />
          )}

          {(status === "denied" || values.status === "denied") && (
            <Field
              component={AdministrateSelectInput}
              resourceUrl="/admin/enums/denial_reason_options.json"
              label="Denial Reason"
              name="denialReasonCode"
              labelKey="label"
            />
          )}
        </>
      )}

      {values.createdAt && (
        <div className="bg-info bg-opacity-10 text-center py-2 mb-3 rounded-2">
          <p className="fw-medium mb-0">Created on {formatDateTime(values.createdAt)}</p>
        </div>
      )}

      <LoadingButton loading={submitting} onClick={handleSubmit} disabled={submitting}>
        Save
      </LoadingButton>
    </>
  );
};
